/**
 * SyllabusTree — 강의의 행 나무 한 부품. 관리 페이지(manage)와 시험지 조립 창 덮개(pick)가 함께 쓴다.
 *
 * 얼굴은 시험지 폴더 나무(ExamPapersV2/FolderTree)를 그대로 따른다 — 고른 행은 옅은 초록 둥근 면에 굵은 초록 글자,
 * 펼침 손잡이는 둥근 회색, 하위 행은 한 단에 12px(pl-3) 들여쓴다. 같은 「나무」가 두 화면에서 다른 얼굴이면
 * 같은 조작인 줄 모른다.
 *
 * 행은 두 종류다: 조건 행(누르면 그 조건으로 찾는다 — 거름 아이콘, 둘째 줄에 조건 요약)과 조건 없는 제목 행(거름망에 X 아이콘 + 하위 수 — 2026-09-14 폴더에서 바꿈).
 * 요약은 받아 둔 식에서 부르는 쪽이 만든다(summaryOf) — 행마다 건수를 세려고 검색을 쏘지 않는다(설계 §W01).
 * 행 끝 자리(renderTrailing)도 부르는 쪽이 채운다 — 학습 화면 「강의」 보기가 난이도 배지를 단다(FE-20260914 §3).
 * 넘기지 않으면 아무것도 그리지 않아, 강의 관리 화면·시험지 조립 창의 나무는 그대로다.
 * 펼침 상태는 부르는 쪽이 들고 있다(세션 저장 — 공유되는 물건 위에 보기 상태를 얹지 않는다, 판 2 §3-3).
 *
 * 손으로 목차를 치는 자리다(오너 결정 2026-09-13): 이름을 고치거나 새 행 이름을 치고 Enter 면 곧바로 저장하고,
 * 새 행은 저장되자마자 그 아래에 다음 입력칸이 열린다. Tab·Shift+Tab 으로 들여쓰고 내어쓴다(문서 편집기의 목차 치기와 같은 손).
 * 조건(식)만 초안이다 — 그건 작업 칸의 [저장]이 맡는다.
 *
 * 편집 손잡이([+ 하위] · [⋯])는 고른 행·마우스를 올린 행·키보드 초점이 있는 행에만 보인다. 모든 행에 늘어놓으면
 * 이름 자리가 줄고 어느 행의 손잡이인지 헷갈린다. 위·아래·옮기기·지우기·제목행 전환은 [⋯] 판으로 보낸다.
 *
 * 키보드는 WAI-ARIA tree 규칙(ul role=tree · li role=treeitem, 초점 행 하나만 tabIndex 0). **초점 이동만으로는 고르지 않는다** —
 * 고르면 검색이 나가기 때문이다(설계 §W08·§5 표). Enter·Space 에서만 고른다.
 *
 * 끌어 놓기는 시험지 폴더 나무(ExamPapersV2/FolderTree)의 방식을 따른다(오너 지시 2026-09-13) — 전용 MIME 이 실린 끌기만 받고,
 * 놓을 수 없는 자리(자기·자기 하위 · 다섯 단 초과)는 dragover 에서 막아 브라우저가 금지 커서를 그린다. 서버(400)를 눌러 보고 알게 하지 않는다.
 * 폴더와 다른 것은 **순서**다 — 폴더는 순서를 정하는 길이 없어 「어느 폴더 안으로」만 고르지만, 강의 행은 목차라 순서가 있다.
 * 그래서 줄 가운데에 놓으면 그 행의 끝 하위로(고리), 위·아래 가장자리에 놓으면 그 사이로(선) 끼운다(오너 결정 2026-09-13).
 */
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown, ChevronRight, FilterX, ListFilter, Plus, MoreHorizontal,
  Pencil, CornerDownRight, ArrowUp, ArrowDown, FolderInput, Trash2, X,
} from 'lucide-react';
import {
  buildIndex, childrenOf, parentOf, ancestorsOf, siblingsOf, descendantsOf, canAddChild, filterIds, dropDestination,
  MAX_DEPTH, MAX_NODES,
} from './treeIndex';
import { describeSyllabusError } from './syllabusApi';
import TreeRowMenu from './TreeRowMenu';
import MathName from './MathName';

/** 행 이름 한도 — 서버 계약(회신 §4, 1~200자) */
export const NAME_MAX = 200;

const DEPTH_MSG = `행은 ${MAX_DEPTH}단까지입니다.`;
const FULL_MSG = `한 강의의 행은 ${MAX_NODES}개까지입니다.`;
const BUSY_MSG = '앞의 저장이 끝나면 할 수 있습니다.';

/** 행 버튼 — FolderTree 의 rowClass(active, {face:false}) 와 같은 값. 고른 면은 감싸는 <li> 가 깐다 */
const rowClass = (active, twoLine) => `flex w-full min-w-0 ${twoLine ? 'items-start' : 'items-center'} gap-1.5 rounded-xl px-2 py-1.5 text-left text-xs transition-colors ${
  active ? 'font-bold text-primary' : 'text-gray-700 hover:bg-gray-50'
}`;
const handleCls = 'shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40';
/** 나무 안 입력 — FolderTree 이름 고치기 선례(글자 12px · 위아래 4px). 행 글자가 12px 라 나무 안에서는 이 크기다 */
const inputCls = 'w-full min-w-0 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs focus:border-primary focus:outline-none read-only:bg-gray-50';
/** 바닥 [최상위 행 추가] — 좌우를 채운 점선 상자. 점선 색 · 모서리는 끌 때 뜨는 「최상위로」 띠와 같게 둔다(한 나무에서 「최상위」의 얼굴은 하나).
    잠겼을 때는 마우스를 올려도 초록으로 바뀌지 않는다(enabled:hover) */
const addRootBtnCls = 'flex w-full items-center justify-center gap-1 rounded-xl border border-dashed border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-500 transition-colors enabled:hover:border-primary enabled:hover:bg-primary/5 enabled:hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40';

const idOf = (v) => (v == null ? null : String(v));
const newRowKeyOf = (nr) => (nr ? `${idOf(nr.parentId) ?? 'root'}|${idOf(nr.afterId) ?? 'end'}` : null);
/** 한글 조합 중의 Enter 는 글자를 확정하는 키다 — 저장·만들기로 치지 않는다 */
const composing = (e) => Boolean(e.nativeEvent?.isComposing) || e.keyCode === 229;

/**
 * 행 끌기 페이로드 MIME — 폴더 나무의 FOLDER_DND_MIME 과 같은 이유로 전용 이름을 쓴다.
 * dragover 중에는 보안상 getData 가 막히고 types 만 읽혀, 「우리 행을 끌고 있나」를 가를 수단이 이것뿐이다.
 */
export const ROW_DND_MIME = 'application/x-ky-syllabus-row';
/** 최상위 띠의 표식 — 행 id 와 섞이지 않게 문자열로 둔다 */
const ROOT_DROP = '__root__';
/** 줄 높이에서 위·아래 가장자리로 치는 몫 — 그 사이(가운데 절반)는 「안으로」 */
const EDGE = 0.25;

function SyllabusTree({
  items = [],
  selectedId = null,
  expanded,
  onToggle,
  onSelect,
  mode = 'manage',
  readOnly = false,
  emptyText = '행이 없습니다.',
  summaryOf = null,
  dirtyIds = null,
  filterText = '',
  extraText = null,
  onRename = null,
  newRow = null,
  onNewRowChange = null,
  onCreate = null,
  onRetryOrder = null,
  onMove = null,
  onReparentRequest = null,
  onDelete = null,
  onConvert = null,
  onMoveTo = null,
  rootDropHost = null,
  busy = false,
  renderTrailing = null,
}) {
  const index = useMemo(() => buildIndex(items), [items]);
  const open = useMemo(() => (expanded instanceof Set ? expanded : new Set()), [expanded]);
  const pick = mode === 'pick';
  const editable = mode === 'manage' && !readOnly;
  const canCreate = editable && typeof onCreate === 'function' && typeof onNewRowChange === 'function';
  const canRename = editable && typeof onRename === 'function';
  const canDrag = editable && typeof onMoveTo === 'function';
  const full = items.length >= MAX_NODES;
  const sel = selectedId != null && index.byId.has(String(selectedId)) ? String(selectedId) : null;

  // ── 나무 안 찾기 — 맞은 행과 그 조상만, 조상은 펼친 것처럼 ──
  const filter = useMemo(
    () => filterIds(index, filterText, extraText || (() => '')),
    [index, filterText, extraText],
  );

  const [focusedId, setFocusedId] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  const [rename, setRenameState] = useState(null); // { id, text, saving, error }
  const [draft, setDraft] = useState({ text: '', busy: false, error: null, blocked: null }); // 새 행 입력칸
  const [notices, setNotices] = useState([]); // [{ key, kind:'order'|'error', nodeId, afterId, name, message, busy }]
  const [focusNewTick, setFocusNewTick] = useState(0);
  const [dragId, setDragId] = useState(null); // 지금 끄는 행 — 흐림·최상위 띠를 그릴지 판정
  const [dropAt, setDropAt] = useState(null); // { id, zone, dest, firstKid } | { id: ROOT_DROP, dest } — 놓일 자리 표시

  const rowRefs = useRef(new Map());
  const pendingFocus = useRef(null);
  const menuAnchorRef = useRef(null);
  const renameRef = useRef(null);
  const renameInputRef = useRef(null);
  const newInputRef = useRef(null);
  const newRowRef = useRef(newRow);
  const newPhase = useRef('idle'); // 'idle' | 'creating' | 'moving' — 만드는 중·다음 칸으로 옮기는 중의 blur 는 사용자의 떠남이 아니다
  const leftWhileCreating = useRef(false);
  const keepText = useRef(false);
  const aliveRef = useRef(true);
  const noticeSeq = useRef(0);
  // 끄는 행 — 판정은 dragover 시점에 **동기로** 필요하다(state 는 다음 렌더에나 반영돼 늦다. 폴더 나무와 같은 이유)
  const dragRef = useRef(null);

  newRowRef.current = newRow;
  // 세울 때 다시 켠다 — 개발 모드(StrictMode)는 효과를 한 번 걷었다 다시 세우므로, 끄기만 하면 응답을 영영 버린다
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const setRename = useCallback((next) => {
    renameRef.current = next;
    setRenameState(next);
  }, []);

  // 부르는 쪽이 고른 행을 바꾸면 키보드 초점 기억을 푼다 — 다시 들어오면 고른 행에서 시작한다
  useEffect(() => { setFocusedId(null); }, [sel]);

  // ── 보이는 행 ──
  const newRowParent = canCreate && newRow ? idOf(newRow.parentId) : undefined;
  const newRowShown = newRowParent !== undefined
    && (newRowParent == null || (index.byId.has(newRowParent) && (!filter || filter.visible.has(newRowParent))));
  const kidsOf = useCallback(
    (id) => (filter ? childrenOf(index, id).filter((k) => filter.visible.has(k)) : childrenOf(index, id)),
    [index, filter],
  );
  const { rowIds, meta } = useMemo(() => {
    const ids = [];
    const m = new Map();
    const walk = (list, depth) => list.forEach((id) => {
      const kids = kidsOf(id);
      const isOpen = kids.length > 0 && (filter ? true : (open.has(id) || (newRowShown && newRowParent === id)));
      ids.push(id);
      m.set(id, { depth, kids, open: isOpen });
      if (isOpen) walk(kids, depth + 1);
    });
    walk(kidsOf(null), 1);
    return { rowIds: ids, meta: m };
  }, [kidsOf, filter, open, newRowShown, newRowParent]);

  const rovingId = (focusedId != null && meta.has(focusedId) && focusedId)
    || (sel != null && meta.has(sel) && sel)
    || rowIds[0]
    || null;

  // ── 초점 ──
  const focusRow = useCallback((id) => {
    if (id == null) return;
    const k = String(id);
    setFocusedId(k);
    const el = rowRefs.current.get(k);
    if (el) el.focus();
    else pendingFocus.current = k; // 방금 펼쳐 아직 서지 않은 행 — 그린 뒤에 옮긴다
  }, []);
  useEffect(() => {
    const k = pendingFocus.current;
    if (k == null) return;
    const el = rowRefs.current.get(k);
    if (el) { pendingFocus.current = null; el.focus(); }
  });

  // ── 이름 고치기 ──
  const startRename = useCallback((id) => {
    const it = index.byId.get(String(id));
    // 앞 이름이 저장 중이면 새 칸을 열지 않는다 — 칸을 바꿔 끼우면 앞 저장의 실패 사유를 보일 자리가 사라진다
    if (!canRename || !it || renameRef.current?.saving) return;
    setMenuFor(null);
    setRename({
      id: String(id), text: it.name ?? '', saving: false, error: null,
    });
  }, [index, canRename, setRename]);

  // 열리면 입력에 초점 + 전부 선택. 판(⋯)에서 열었으면 판이 닫히며 [⋯]로 초점을 돌려주므로(setTimeout 0) 그 뒤에 옮긴다
  const renameOpenId = rename?.id ?? null;
  useEffect(() => {
    if (renameOpenId == null) return undefined;
    const t = setTimeout(() => {
      const el = renameInputRef.current;
      if (el) { el.focus(); el.select(); }
    }, 0);
    return () => clearTimeout(t);
  }, [renameOpenId]);

  // 지워진 행의 편집·판은 닫는다
  useEffect(() => {
    if (rename && !index.byId.has(rename.id)) setRename(null);
    if (menuFor != null && !index.byId.has(menuFor)) setMenuFor(null);
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitRename = async (via) => {
    const r = renameRef.current;
    if (!r || r.saving) return;
    const it = index.byId.get(r.id);
    const name = r.text.trim();
    // 빈 이름·한도 초과·그대로면 보내지 않고 닫는다
    if (!it || !name || name.length > NAME_MAX || name === it.name) {
      setRename(null);
      if (via === 'enter') focusRow(r.id);
      return;
    }
    setRename({ ...r, saving: true, error: null });
    try {
      await onRename(r.id, name);
      if (!aliveRef.current) return;
      // 기다리는 사이 다른 곳으로 떠났으면 초점을 끌어오지 않는다 — 칸이 아직 초점을 쥐고 있을 때만 행으로 돌려준다
      const stay = via === 'enter' && renameInputRef.current != null && document.activeElement === renameInputRef.current;
      if (renameRef.current?.id === r.id) setRename(null);
      if (stay) focusRow(r.id);
    } catch (e) {
      if (!aliveRef.current) return;
      if (renameRef.current?.id === r.id) {
        setRename({ ...renameRef.current, saving: false, error: describeSyllabusError(e) || '이름을 저장하지 못했습니다.' });
      }
    }
  };

  const onRenameKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (composing(e)) return;
      e.preventDefault();
      e.stopPropagation();
      commitRename('enter');
    } else if (e.key === 'Escape') {
      // 강의 덮개·조립 창도 Escape 로 닫힌다 — 이 편집만 취소한다
      e.preventDefault();
      e.stopPropagation();
      const r = renameRef.current;
      if (!r || r.saving) return;
      setRename(null);
      focusRow(r.id);
    }
  };

  // ── 새 행 입력(연달아 치기) ──
  const newRowKey = newRowKeyOf(canCreate ? newRow : null);

  // 새 행이 설 부모(와 그 조상)를 연다 — 접힌 가지 안에 입력칸이 태어나면 보이지 않는다
  useEffect(() => {
    if (!newRowShown || newRowParent == null || typeof onToggle !== 'function') return;
    [...ancestorsOf(index, newRowParent), newRowParent].forEach((a) => { if (!open.has(a)) onToggle(a, true); });
  }, [newRowKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // 자리가 바뀌면 글자를 비우고(들여쓰기·내어쓰기는 글자를 들고 간다) 입력에 초점.
  // setTimeout 0 — 판(⋯)에서 열었으면 판이 [⋯]로 초점을 돌려준 뒤에 옮겨야 입력이 곧바로 blur 로 닫히지 않는다
  useEffect(() => {
    if (newRowKey == null) {
      newPhase.current = 'idle';
      return undefined;
    }
    if (!keepText.current) setDraft({ text: '', busy: false, error: null, blocked: null });
    else setDraft((d) => ({ ...d, error: null }));
    keepText.current = false;
    const t = setTimeout(() => {
      if (newInputRef.current) newInputRef.current.focus();
      newPhase.current = 'idle';
    }, 0);
    return () => clearTimeout(t);
  }, [newRowKey, focusNewTick]);

  const closeNewRow = (focusBack) => {
    const nr = newRowRef.current;
    onNewRowChange(null);
    if (focusBack && nr) focusRow(idOf(nr.afterId) ?? idOf(nr.parentId));
  };

  const pushNotice = (n) => {
    noticeSeq.current += 1;
    const key = `n${noticeSeq.current}`;
    setNotices((list) => [...list, { ...n, key, busy: false }]);
  };

  const createFromDraft = async (via) => {
    const nr = newRowRef.current;
    if (!nr || newPhase.current !== 'idle') return;
    const name = draft.text.trim();
    if (!name) { closeNewRow(via === 'enter'); return; }
    if (full) { setDraft((d) => ({ ...d, blocked: FULL_MSG })); return; }
    const at = newRowKeyOf(nr);
    newPhase.current = 'creating';
    leftWhileCreating.current = via === 'blur';
    setDraft((d) => ({
      ...d, busy: true, error: null, blocked: null,
    }));
    try {
      const res = await onCreate({ parentId: nr.parentId ?? null, afterId: nr.afterId ?? null, name });
      if (!aliveRef.current) return;
      if (res?.orderFailed) {
        pushNotice({
          kind: 'order', nodeId: idOf(res.nodeId), afterId: idOf(nr.afterId), name, message: res.error || '',
        });
      }
      // 만드는 사이 사용자가 다른 자리를 열었으면 그 자리를 건드리지 않는다
      if (newRowKeyOf(newRowRef.current) !== at) { newPhase.current = 'idle'; return; }
      if (via === 'enter' && !leftWhileCreating.current && res?.nodeId != null) {
        // 바로 그 아래(같은 부모)에 다음 입력칸 — 목차를 손으로 연달아 친다
        newPhase.current = 'moving';
        setDraft({
          text: '', busy: false, error: null, blocked: null,
        });
        setFocusNewTick((n) => n + 1);
        onNewRowChange({ parentId: idOf(nr.parentId), afterId: idOf(res.nodeId) });
      } else {
        newPhase.current = 'idle';
        setDraft({
          text: '', busy: false, error: null, blocked: null,
        });
        onNewRowChange(null);
      }
    } catch (e) {
      if (!aliveRef.current) return;
      newPhase.current = 'idle';
      const message = describeSyllabusError(e) || '행을 만들지 못했습니다.';
      if (newRowKeyOf(newRowRef.current) !== at) {
        // 입력칸은 이미 다른 자리 — 친 이름을 잃지 않게 안내로 남긴다
        pushNotice({
          kind: 'error', nodeId: null, afterId: null, name, message,
        });
        return;
      }
      setDraft((d) => ({ ...d, busy: false, error: message })); // 글자는 그대로
    }
  };

  const indent = () => {
    const nr = newRowRef.current;
    const pk = idOf(nr.parentId);
    const sibs = childrenOf(index, pk);
    const after = idOf(nr.afterId);
    const pos = after != null && sibs.includes(after) ? sibs.indexOf(after) : sibs.length - 1;
    const above = pos >= 0 ? sibs[pos] : null;
    if (above == null) { setDraft((d) => ({ ...d, blocked: '위에 같은 단의 행이 없어 들여쓸 수 없습니다.' })); return; }
    if (!canAddChild(index, above)) { setDraft((d) => ({ ...d, blocked: DEPTH_MSG })); return; }
    keepText.current = true;
    setDraft((d) => ({ ...d, blocked: null }));
    onNewRowChange({ parentId: above, afterId: null }); // 그 행의 마지막 자식 다음
  };

  const outdent = () => {
    const nr = newRowRef.current;
    const pk = idOf(nr.parentId);
    if (pk == null) { setDraft((d) => ({ ...d, blocked: '이미 최상위 행입니다.' })); return; }
    keepText.current = true;
    setDraft((d) => ({ ...d, blocked: null }));
    onNewRowChange({ parentId: parentOf(index, pk), afterId: pk }); // 지금 부모의 바로 다음
  };

  const onNewKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (composing(e)) return;
      e.preventDefault();
      e.stopPropagation();
      if (!draft.busy) createFromDraft('enter');
    } else if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      if (draft.busy) return;
      if (e.shiftKey) outdent(); else indent();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (!draft.busy) closeNewRow(true);
    }
  };

  const onNewBlur = () => {
    if (newPhase.current === 'creating') { leftWhileCreating.current = true; return; }
    if (newPhase.current !== 'idle' || !newRowRef.current) return;
    if (draft.text.trim()) createFromDraft('blur'); // 만들고 닫는다 — 다음 칸은 열지 않는다
    else onNewRowChange(null);
  };

  const retryOrder = async (n) => {
    setNotices((list) => list.map((x) => (x.key === n.key ? { ...x, busy: true } : x)));
    try {
      // 옮기기는 같은 부름을 한 번 더 — 부모가 이미 맞으므로 순서(N4)만 나간다
      const res = n.op === 'move' ? await onMoveTo(n.nodeId, n.dest) : await onRetryOrder(n.nodeId, n.afterId);
      if (!aliveRef.current) return;
      if (res?.orderFailed) {
        setNotices((list) => list.map((x) => (x.key === n.key ? { ...x, busy: false, message: res.error || '' } : x)));
        return;
      }
      setNotices((list) => list.filter((x) => x.key !== n.key));
    } catch (e) {
      if (aliveRef.current) {
        setNotices((list) => list.map((x) => (x.key === n.key ? { ...x, busy: false, message: describeSyllabusError(e) } : x)));
      }
    }
  };

  // ── 행 누르기 · 키보드 ──

  /** 행을 누름·Enter·Space — pick 모드의 제목 행은 펼침/접힘뿐(찾을 조건이 없다), 나머지는 onSelect */
  const activate = (id) => {
    const it = index.byId.get(id);
    const m = meta.get(id);
    if (!it) return;
    if (pick && it.query == null) {
      if (m && m.kids.length && !filter && onToggle) onToggle(id, !m.open);
      return;
    }
    if (onSelect) onSelect(it);
  };

  const onTreeKeyDown = (e) => {
    const t = e.target;
    const li = t && t.closest ? t.closest('[role="treeitem"]') : null;
    // 행 자체(또는 누른 뒤 초점이 남은 행 버튼)에서 난 키만 — 입력칸·손잡이의 키는 그쪽 몫이다
    if (!li || !(t === li || t.hasAttribute('data-tree-row'))) return;
    if (composing(e)) return;
    const id = li.getAttribute('data-node-id');
    const m = meta.get(id);
    if (!m) return;
    const pos = rowIds.indexOf(id);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (pos >= 0 && pos < rowIds.length - 1) focusRow(rowIds[pos + 1]);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (pos > 0) focusRow(rowIds[pos - 1]);
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (m.kids.length && !m.open) { if (onToggle) onToggle(id, true); } else if (m.open) focusRow(m.kids[0]);
        break;
      case 'ArrowLeft': {
        e.preventDefault();
        if (m.open && !filter) { if (onToggle) onToggle(id, false); break; }
        const p = parentOf(index, id);
        if (p != null && meta.has(p)) focusRow(p);
        break;
      }
      case 'Enter':
      case ' ':
        if (t !== li) return; // 행 버튼은 제 click 으로 고른다 — 두 번 부르지 않는다
        e.preventDefault();
        activate(id);
        break;
      case 'F2':
        if (!canRename) return;
        e.preventDefault();
        startRename(id);
        break;
      default:
    }
  };

  const onTreeFocus = (e) => {
    const t = e.target;
    if (!t || !t.closest || t.closest('[data-new-row]')) return;
    const li = t.closest('[role="treeitem"]');
    if (li) setFocusedId(li.getAttribute('data-node-id'));
  };

  // ── 끌어 놓기 ──

  const isRowDrag = (e) => Array.from(e.dataTransfer?.types || []).includes(ROW_DND_MIME);

  /** 끌고 있는 행 id — 페이로드가 먼저고, 못 읽으면 끌기 시작 때 적어 둔 값(폴더 나무와 같은 순서) */
  const draggedId = (e) => {
    let payload = null;
    try { payload = JSON.parse(e.dataTransfer.getData(ROW_DND_MIME)); } catch (_) { payload = null; }
    return idOf(payload?.id ?? dragRef.current);
  };

  /**
   * 줄 위 어느 자리에 놓이는가 — 가운데 절반은 그 행 안(끝 하위)으로, 위·아래 1/4 은 그 사이로.
   * 안으로 못 가면(다섯 단 초과 등) 줄을 반으로 갈라 사이만 받는다 — 가운데가 통째로 금지 자리가 되지 않게.
   * 판정 자체(자기·하위 금지 · 깊이 · 제자리)는 treeIndex.dropDestination 이 쥔다 — [옮기기…] 목록과 같은 자다.
   */
  const dropAtRow = (e, id, src) => {
    if (src == null) return null;
    const r = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - r.top;
    const m = meta.get(id);
    const kid = m && m.open && m.kids.length ? m.kids[0] : null;
    let zone = 'inside';
    if (y < r.height * EDGE) zone = 'before';
    else if (y > r.height * (1 - EDGE)) zone = 'after';
    let dest = dropDestination(index, src, id, zone, kid);
    if (!dest && zone === 'inside') {
      zone = y < r.height / 2 ? 'before' : 'after';
      dest = dropDestination(index, src, id, zone, kid);
    }
    return dest ? {
      id, zone, dest, firstKid: zone === 'after' && kid != null,
    } : null;
  };

  const endDrag = () => {
    dragRef.current = null;
    setDragId(null);
    setDropAt(null);
  };

  /**
   * 옮기기 — 부르는 쪽(작업 공간 moveTo)이 N2·N4 를 줄 세운다. 낙관적으로 그리지 않는다: 응답이 원본을 고치면 나무가 그렇게 선다.
   * 온전히 실패하면 빨간 안내, 부모는 옮겨졌는데 순서만 실패하면 새 행 만들기와 같은 「순서 반영 실패 [다시 시도]」.
   */
  const runMove = async (src, dest) => {
    const name = index.byId.get(src)?.name ?? '';
    try {
      const res = await onMoveTo(src, dest);
      if (!aliveRef.current) return;
      // 접힌 행 안으로 넣었으면 편다 — 옮긴 행이 어디 갔는지 화면에 남게
      if (dest.parentId != null && typeof onToggle === 'function' && !open.has(dest.parentId)) onToggle(dest.parentId, true);
      if (res?.orderFailed) {
        pushNotice({
          kind: 'order', op: 'move', nodeId: src, dest, name, message: res.error || '',
        });
      }
    } catch (e) {
      if (!aliveRef.current) return;
      pushNotice({
        kind: 'error', op: 'move', nodeId: null, name, message: describeSyllabusError(e) || '행을 옮기지 못했습니다.',
      });
    }
  };

  /** 행 줄이 받는 끌기 — 폴더 나무 dropProps 와 같은 골격(우리 MIME 만 받고, 막는 자리는 preventDefault 전에 빠진다) */
  const rowDropProps = (id) => ({
    onDragOver: (e) => {
      if (!isRowDrag(e)) return;
      const at = dropAtRow(e, id, dragRef.current);
      if (!at) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dropAt?.id !== id || dropAt?.zone !== at.zone) setDropAt(at);
    },
    onDragLeave: (e) => {
      // 줄 안의 자식 요소로 옮겨 갈 때도 leave 가 난다 — 실제로 이 줄을 벗어난 것만 끈다
      if (e.currentTarget.contains(e.relatedTarget)) return;
      setDropAt((cur) => (cur && cur.id === id ? null : cur));
    },
    onDrop: (e) => {
      if (!isRowDrag(e)) return;
      e.preventDefault();
      const src = draggedId(e);
      const at = dropAtRow(e, id, src); // 놓는 순간의 자리로 다시 잰다 — 끄는 사이 나무가 다시 읽혔을 수 있다
      endDrag();
      if (at) runMove(src, at.dest);
    },
  });

  /** 최상위로 올리는 띠 — 하위 행을 끌 때만 뜬다(최상위 행은 이미 최상위다). 놓으면 최상위의 끝 */
  const rootDropProps = {
    onDragOver: (e) => {
      if (!isRowDrag(e) || !dropDestination(index, dragRef.current, null)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dropAt?.id !== ROOT_DROP) setDropAt({ id: ROOT_DROP, dest: { parentId: null, beforeId: null } });
    },
    onDragLeave: (e) => {
      if (e.currentTarget.contains(e.relatedTarget)) return;
      setDropAt((cur) => (cur && cur.id === ROOT_DROP ? null : cur));
    },
    onDrop: (e) => {
      if (!isRowDrag(e)) return;
      e.preventDefault();
      const src = draggedId(e);
      const dest = src != null ? dropDestination(index, src, null) : null;
      endDrag();
      if (dest) runMove(src, dest);
    },
  };

  // ── 행 더보기 ──
  const menuActions = (id) => {
    const it = index.byId.get(id);
    const isTitle = it?.query == null;
    const sib = siblingsOf(index, id);
    const k = sib.indexOf(id);
    const childOk = canAddChild(index, id);
    const run = (fn) => () => { setMenuFor(null); fn(); };
    const addReason = full ? FULL_MSG : '이 화면에서는 행을 만들 수 없습니다.';
    const renameSaving = Boolean(rename?.saving);
    return [
      {
        key: 'rename',
        label: '이름 바꾸기',
        Icon: Pencil,
        disabled: !canRename || renameSaving,
        reason: canRename ? '앞의 이름 저장이 끝나면 할 수 있습니다.' : '이 화면에서는 이름을 바꿀 수 없습니다.',
        onSelect: run(() => startRename(id)),
      },
      {
        key: 'sibling', label: '형제 행 추가', Icon: Plus, disabled: !canCreate || full, reason: addReason, onSelect: run(() => onNewRowChange({ parentId: parentOf(index, id), afterId: id })),
      },
      {
        key: 'child',
        label: '하위 행 추가',
        Icon: CornerDownRight,
        disabled: !canCreate || full || !childOk,
        reason: !childOk ? DEPTH_MSG : addReason,
        onSelect: run(() => onNewRowChange({ parentId: id, afterId: null })),
      },
      {
        key: 'up', label: '위로', Icon: ArrowUp, disabled: !onMove || busy || k <= 0, reason: k <= 0 ? '맨 위 행입니다.' : BUSY_MSG, onSelect: run(() => onMove(id, -1)),
      },
      {
        key: 'down',
        label: '아래로',
        Icon: ArrowDown,
        disabled: !onMove || busy || k < 0 || k >= sib.length - 1,
        reason: k >= sib.length - 1 ? '맨 아래 행입니다.' : BUSY_MSG,
        onSelect: run(() => onMove(id, +1)),
      },
      {
        key: 'reparent', label: '옮기기…', Icon: FolderInput, disabled: !onReparentRequest || busy, reason: BUSY_MSG, onSelect: run(() => onReparentRequest(id)),
      },
      {
        key: 'convert',
        label: isTitle ? '이 행에 조건 만들기' : '제목행으로 바꾸기',
        Icon: isTitle ? ListFilter : FilterX,
        disabled: !onConvert || busy,
        reason: BUSY_MSG,
        onSelect: run(() => onConvert(id)),
      },
      {
        key: 'delete', label: '지우기', Icon: Trash2, danger: true, separated: true, disabled: !onDelete || busy, reason: BUSY_MSG, onSelect: run(() => onDelete(id)),
      },
    ];
  };

  // ── 그리기 ──

  const renderNewRow = (depth) => {
    const parentName = newRowParent != null ? index.byId.get(newRowParent)?.name : null;
    const line = draft.error || draft.blocked;
    return (
      <li key="__new-row__" role="none" data-new-row="">
        {/* 입력칸은 그 단의 폭을 좌우로 꽉 채운다 — 바닥 [최상위 행 추가] 와 같은 폭(오너 2026-09-14).
            + 는 칸 안 왼쪽에. 아이콘은 relative 상자의 직계 자식이어야 제자리에 서고(전역 preserve-3d), **입력칸 뒤에** 적어야
            보인다 — 앞에 두면 입력칸의 흰 바탕이 덮는다(2026-09-14 실측, 위 「진도 행 찾기」 칸의 돋보기가 그렇게 가려져 있다) */}
        <div className="relative py-0.5">
          <input
            ref={newInputRef}
            type="text"
            value={draft.text}
            maxLength={NAME_MAX}
            readOnly={draft.busy}
            aria-busy={draft.busy || undefined}
            aria-invalid={draft.error ? true : undefined}
            aria-label={parentName ? `새 행 이름 — ${parentName} 아래 ${depth}단` : `새 행 이름 — 최상위`}
            placeholder="새 행 이름"
            onChange={(e) => {
              const v = e.target.value;
              setDraft((d) => ({
                ...d, text: v, error: null, blocked: null,
              }));
            }}
            onKeyDown={onNewKeyDown}
            onBlur={onNewBlur}
            className={`${inputCls} pl-7`}
          />
          <Plus aria-hidden="true" className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
        </div>
        {line ? (
          <p role={draft.error ? 'alert' : 'status'} className={`pb-1 pl-7 pr-2 text-[11px] ${draft.error ? 'text-red-600' : 'text-amber-700'}`}>{line}</p>
        ) : (
          <p className="pb-1 pl-7 pr-2 text-[10px] text-gray-400">
            {draft.busy ? '저장하는 중…' : 'Enter 저장 후 다음 행 · Tab 들여쓰기 · Shift+Tab 내어쓰기 · Esc 닫기'}
          </p>
        )}
      </li>
    );
  };

  const renderChildren = (parentKey, depth) => {
    const nodes = (parentKey == null ? kidsOf(null) : (meta.get(parentKey)?.kids || [])).map((k) => renderNode(k, depth));
    if (newRowShown && newRowParent === parentKey) {
      const list = parentKey == null ? kidsOf(null) : (meta.get(parentKey)?.kids || []);
      const after = idOf(newRow.afterId);
      const at = after != null ? list.indexOf(after) : -1;
      nodes.splice(at >= 0 ? at + 1 : nodes.length, 0, renderNewRow(depth));
    }
    return nodes;
  };

  const renderNode = (id, depth) => {
    const it = index.byId.get(id);
    const m = meta.get(id);
    if (!it || !m) return null;
    const active = sel === id;
    const isTitle = it.query == null;
    const count = childrenOf(index, id).length;
    const summary = summaryOf ? (summaryOf(it) || '') : '';
    const dirty = dirtyIds instanceof Set && dirtyIds.has(id);
    const twoLine = Boolean(summary) || dirty;
    // 제목 행 = 조건 없음 → 거름망에 X(오너 2026-09-14, 종전 폴더). 조건 행의 거름망과 한 쌍으로 읽힌다.
    // 열림·닫힘은 왼쪽 화살표가 이미 말하므로 아이콘은 하나다
    const Icon = isTitle ? FilterX : ListFilter;
    const renamingHere = rename != null && rename.id === id;
    const trailing = typeof renderTrailing === 'function' && !renamingHere ? renderTrailing(it) : null;
    const showHandles = editable && !renamingHere;
    const handlesOn = active || focusedId === id || menuFor === id;
    const hereNew = newRowShown && newRowParent === id;
    const dropHere = dropAt != null && dropAt.id === id ? dropAt : null;
    // 줄의 이름(aria-label)은 안의 글자를 덮는다 — 둘째 줄의 요약·「변경」을 제목 행에도 빠짐없이 싣는다
    // (제목 행에 「이 행에 조건 만들기」로 초안이 얹히면 둘째 줄이 선다)
    const label = `${it.name}${isTitle ? ` — 제목 행${count ? ` · ${count}개 행` : ''}` : ''}${summary ? ` — ${summary}` : ''}${dirty ? ' · 변경' : ''}`;
    return (
      <li
        key={id}
        ref={(el) => { if (el) rowRefs.current.set(id, el); else rowRefs.current.delete(id); }}
        role="treeitem"
        data-node-id={id}
        aria-level={depth}
        aria-expanded={m.kids.length ? m.open : undefined}
        aria-selected={active}
        aria-label={label}
        tabIndex={id === rovingId ? 0 : -1}
        className={`focus:outline-none ${active ? 'rounded-xl bg-primary/10' : ''}`}
      >
        <div
          className={`group relative flex min-w-0 ${twoLine ? 'items-start' : 'items-center'} gap-0.5 rounded-xl [li:focus-visible>&]:ring-2 [li:focus-visible>&]:ring-primary`}
          {...(canDrag && !renamingHere ? rowDropProps(id) : {})}
        >
          {/* 사이에 놓일 자리 — 줄 위·아래 가장자리의 선. 이름 열(펼침 손잡이 22px + 틈 2px)에서 시작하고,
              펼친 행의 아래는 첫 하위 자리라 한 단(12px) 더 들여 긋는다 */}
          {dropHere && dropHere.zone !== 'inside' && (
            <span
              aria-hidden="true"
              data-drop-line={dropHere.zone}
              className={`pointer-events-none absolute right-1 h-0.5 rounded-full bg-primary ${dropHere.firstKid ? 'left-9' : 'left-6'} ${dropHere.zone === 'before' ? 'top-0' : 'bottom-0'}`}
            />
          )}
          {m.kids.length > 0 ? (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => { if (onToggle) onToggle(id, !m.open); }}
              disabled={Boolean(filter)}
              aria-label={`${it.name} ${m.open ? '접기' : '펼치기'}`}
              title={filter ? '찾는 동안은 맞는 행의 가지를 모두 펼쳐 보입니다.' : undefined}
              className={`${handleCls} ${twoLine ? 'mt-0.5' : ''}`}
            >
              {m.open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            </button>
          ) : isTitle ? (
            // 빈 폴더(제목 행)에도 옅은 화살표 — 같은 단의 폴더와 왼쪽 모양을 같게 둔다. 없으면 막 만든 최상위 폴더가
            // 바로 위 폴더의 하위 행처럼 읽혔다(오너 2026-09-14). 펼칠 것이 없어 단추가 아니다
            <span aria-hidden="true" data-empty-arrow="" className={`grid size-[22px] shrink-0 place-items-center text-gray-300 ${twoLine ? 'mt-0.5' : ''}`}>
              <ChevronRight className="size-3.5" />
            </span>
          ) : (
            // 펼침 손잡이와 같은 폭의 빈자리 — 자식 없는 행의 이름이 옆 행 이름과 같은 세로선에 서게
            <span aria-hidden="true" className={`size-[22px] shrink-0 ${twoLine ? 'mt-0.5' : ''}`} />
          )}
          {renamingHere ? (
            <div className="min-w-0 flex-1 py-0.5 pr-1">
              <input
                ref={renameInputRef}
                type="text"
                value={rename.text}
                maxLength={NAME_MAX}
                readOnly={rename.saving}
                aria-busy={rename.saving || undefined}
                aria-invalid={rename.error ? true : undefined}
                aria-label={`${it.name} 이름`}
                onChange={(e) => setRename({ ...renameRef.current, text: e.target.value, error: null })}
                onKeyDown={onRenameKeyDown}
                onBlur={() => commitRename('blur')}
                className={inputCls}
              />
              {rename.error && <p role="alert" className="mt-0.5 px-1 text-[11px] text-red-600">{rename.error}</p>}
            </div>
          ) : (
            <button
              type="button"
              tabIndex={-1}
              data-tree-row={id}
              onClick={() => { setFocusedId(id); activate(id); }}
              onDoubleClick={() => { if (canRename) startRename(id); }}
              // 앞의 저장이 진행 중이면 끌지 않는다 — 폴더 나무가 편집 중에 끌기를 막는 것과 같은 자리(순서가 앞 쓰기와 엇갈린다)
              draggable={canDrag && !busy}
              onDragStart={canDrag ? (ev) => {
                ev.dataTransfer.setData(ROW_DND_MIME, JSON.stringify({ id }));
                ev.dataTransfer.setData('text/plain', it.name); // 폴백(브라우저 밖으로 끌 때)
                ev.dataTransfer.effectAllowed = 'move';
                dragRef.current = id;
                setDragId(id);
                setMenuFor(null);
              } : undefined}
              onDragEnd={canDrag ? endDrag : undefined}
              title={isTitle ? `${it.name} — 조건 없는 제목 행` : [it.name, summary].filter(Boolean).join('\n')}
              className={`${rowClass(active, twoLine)} ${dropHere?.zone === 'inside' ? 'ring-2 ring-primary ring-offset-1' : ''} ${dragId === id ? 'opacity-40' : ''}`}
            >
              <Icon aria-hidden="true" className={`size-3.5 shrink-0 ${twoLine ? 'mt-px' : ''} ${isTitle ? 'text-gray-400' : ''}`} />
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span data-row-name="" className="min-w-0 truncate"><MathName text={it.name} compact /></span>
                  {isTitle && count > 0 && (
                    <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{count}개 행</span>
                  )}
                </span>
                {twoLine && (
                  <span data-row-summary="" className="mt-0.5 flex min-w-0 text-[11px] font-normal text-gray-500">
                    <span className="min-w-0 truncate"><MathName text={summary} compact /></span>
                    {/* 색만으로 말하지 않는다 — 「변경」 글자를 함께 */}
                    {dirty && <span className="shrink-0 whitespace-pre text-amber-700">{summary ? ' · 변경' : '변경'}</span>}
                  </span>
                )}
              </span>
            </button>
          )}
          {trailing != null && trailing !== false && (
            // 행 끝 자리 — 줄 버튼 밖에 둔다(누르거나 끌어도 고르기·끌기가 아니다). 들여쓰기는 이름 쪽에서만 늘어나
            // 깊이가 달라도 끝 자리는 같은 세로 열에 선다
            <div data-row-trailing="" className={`flex shrink-0 items-center ${twoLine ? 'mt-0.5' : ''}`}>{trailing}</div>
          )}
          {showHandles && (
            <div data-row-actions="" className={`${handlesOn ? 'flex' : 'hidden group-hover:flex'} shrink-0 items-center ${twoLine ? 'mt-0.5' : ''}`}>
              {canCreate && (
                <button
                  type="button"
                  onClick={() => { setFocusedId(id); onNewRowChange({ parentId: id, afterId: null }); }}
                  disabled={full || !canAddChild(index, id)}
                  aria-label={`${it.name} 하위 행 추가`}
                  title={full ? FULL_MSG : (canAddChild(index, id) ? '하위 행 추가' : DEPTH_MSG)}
                  className={handleCls}
                >
                  <Plus className="size-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  menuAnchorRef.current = e.currentTarget;
                  setFocusedId(id);
                  setMenuFor((cur) => (cur === id ? null : id));
                }}
                aria-haspopup="dialog"
                aria-expanded={menuFor === id}
                aria-label={`${it.name} 행 메뉴`}
                title="행 메뉴"
                className={handleCls}
              >
                <MoreHorizontal className="size-3.5" />
              </button>
            </div>
          )}
        </div>
        {((m.open && m.kids.length > 0) || hereNew) && (
          // 하위 묶음 왼쪽 세로선 — 편집기의 들여쓰기 안내선(오너 2026-09-14, 시험지 폴더 나무와 같은 얼굴).
          // 부모 펼침 손잡이(22px)의 가운데 아래로 떨어진다: 10~11px 자리, 하위 행(12px 들여쓰기)과 1px 떨어진다.
          // 고른 행이 있어도 선은 그대로 옅다 — 고른 행은 이미 초록 면이 말한다(강조가 둘이 되지 않게)
          <ul role="group" className="relative space-y-0.5 pl-3 before:pointer-events-none before:absolute before:inset-y-0 before:left-2.5 before:w-px before:bg-gray-200 before:content-['']">{renderChildren(id, depth + 1)}</ul>
        )}
      </li>
    );
  };

  const noMatch = filter != null && filter.visible.size === 0;
  const liveNotices = notices.filter((n) => n.kind !== 'order' || index.byId.has(n.nodeId));
  const menuItem = menuFor != null ? index.byId.get(menuFor) : null;
  const menuDesc = menuItem ? descendantsOf(index, menuFor).length : 0;
  const rootDropVisible = canDrag && dragId != null && parentOf(index, dragId) != null;
  /** 띠를 붙일 자리 — 부르는 쪽이 상자(요소 또는 ref)를 주면 그 상자에 포털로, 안 주면 제자리에 */
  const placeRootStrip = (el) => {
    const host = rootDropHost && 'current' in rootDropHost ? rootDropHost.current : rootDropHost;
    return host ? createPortal(el, host) : el;
  };

  return (
    <div className="min-w-0">
      {!items.length && <p className="px-2 py-6 text-center text-xs text-gray-400">{emptyText}</p>}
      {noMatch && <p className="px-2 py-6 text-center text-xs text-gray-400">맞는 행이 없습니다</p>}
      {!noMatch && (items.length > 0 || (newRowShown && newRowParent == null)) && (
        <ul
          role="tree"
          aria-label="강의 행"
          onKeyDown={onTreeKeyDown}
          onFocus={onTreeFocus}
          className="space-y-0.5"
        >
          {renderChildren(null, 1)}
        </ul>
      )}

      {liveNotices.map((n) => (n.kind === 'order' ? (
        <div key={n.key} role="status" className="mt-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <span className="min-w-0 flex-1">
            「<MathName text={n.name} compact />」 행 {n.op === 'move' ? '옮겨짐' : '생성됨'} · 순서 반영 실패{n.message ? ` — ${n.message}` : ''}
          </span>
          {(n.op === 'move' ? canDrag : onRetryOrder) && (
            <button type="button" onClick={() => retryOrder(n)} disabled={n.busy} className="shrink-0 font-medium underline disabled:opacity-40">
              {n.busy ? '반영 중…' : '다시 시도'}
            </button>
          )}
          <button type="button" onClick={() => setNotices((l) => l.filter((x) => x.key !== n.key))} aria-label="안내 닫기" className="shrink-0 rounded-full p-0.5 hover:bg-amber-100">
            <X className="size-3" />
          </button>
        </div>
      ) : (
        <div key={n.key} role="alert" className="mt-2 flex items-start gap-2 rounded-2xl bg-red-50 p-3 text-xs text-red-600">
          <span className="min-w-0 flex-1">「<MathName text={n.name} compact />」 행을 {n.op === 'move' ? '옮기지' : '만들지'} 못했습니다 — {n.message}</span>
          <button type="button" onClick={() => setNotices((l) => l.filter((x) => x.key !== n.key))} aria-label="안내 닫기" className="shrink-0 rounded-full p-0.5 hover:bg-red-100">
            <X className="size-3" />
          </button>
        </div>
      )))}

      {canCreate && (
        <div className="mt-2">
          {/* 바닥 버튼은 최상위 행(= 최상위 폴더, 새 행은 제목 행으로 생긴다) 하나만 — 하위 행은 각 행 끝 [+] 가,
              고른 행 바로 다음 형제는 행 메뉴 · 새 행 칸 Enter 가 맡는다. 고른 행과 상관없이 늘 최상위 끝에 연다 —
              버튼 이름과 하는 일이 어긋나지 않게(오너 2026-09-14). */}
          <button
            type="button"
            onClick={() => onNewRowChange({ parentId: null, afterId: null })}
            disabled={full}
            title={full ? FULL_MSG : '최상위 끝에 새 행'}
            className={addRootBtnCls}
          >
            <Plus aria-hidden="true" className="size-3.5" /> 최상위 행 추가
          </button>
        </div>
      )}

      {editable && menuItem && (
        <TreeRowMenu
          key={menuFor}
          anchorRef={menuAnchorRef}
          name={menuItem.name}
          caption={`${menuItem.query == null ? '제목 행' : '조건 행'}${menuDesc ? ` · 하위 행 ${menuDesc}개` : ''}`}
          actions={menuActions(menuFor)}
          onClose={() => setMenuFor(null)}
        />
      )}

      {/* 최상위로 올리는 띠 — 폴더 나무와 같은 얼굴·같은 이유로 **자리를 차지하지 않는 덮개**다(흐름 안에 두면 끌기 도중
          목록이 밀려 커서 밑 요소가 바뀌고 드롭이 먹지 않는다 — FolderTree 주석).
          ⚠ 기준 상자에 **포털로 옮겨 붙인다.** 이 앱의 전역 CSS 는 모든 요소(*)에 transform-style: preserve-3d 를 걸어
          모든 요소가 absolute 자식의 기준 상자가 된다 — relative 를 찾아 올라가지 않고 **바로 위 부모**에 붙는다.
          그래서 나무 안에 두면 부르는 쪽이 relative 로 감싸도 띠가 나무 맨 위(첫 행)를 덮는다(2026-09-13 실측: 찾기 칸 114px, 띠 152px).
          폴더 나무의 띠가 제자리에 서는 것은 그 띠가 기준 상자의 **직계 자식**이어서다 — 여기서는 그 조건을 포털로 만든다.
          강의 화면(pages/Syllabus.js)은 찾기 칸부터 나무까지 감싼 상자를 넘겨 이 띠가 **찾기 칸 위**를 덮는다 —
          폴더 나무의 [전체]·[없음]처럼 받지 않는 자리라 잃는 드롭 대상이 없다(오너 결정 2026-09-13). */}
      {/* 감싸는 상자에 흰 바탕(띠와 같은 모서리)을 깐다 — 올렸을 때의 옅은 초록(bg-primary/10)은 반투명이라,
          밑에 깔린 찾기 칸의 안내 글자가 비쳐 띠 문장과 겹쳤다(2026-09-13 실측). 띠 자체의 값은 폴더 나무와 같게 둔다. */}
      {rootDropVisible && placeRootStrip(
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 rounded-xl bg-white">
          <div
            data-root-drop=""
            {...rootDropProps}
            className={`pointer-events-auto rounded-xl border border-dashed px-2 py-1.5 text-center text-[10px] font-medium transition-colors ${
              dropAt?.id === ROOT_DROP ? 'border-primary bg-primary/10 text-primary' : 'border-gray-300 bg-white text-gray-400'
            }`}
          >
            여기에 놓으면 최상위 행이 됩니다
          </div>
        </div>,
      )}
    </div>
  );
}

export default SyllabusTree;
